// Exhaustive-ish catalogue of MySQL / MariaDB built-in functions for editor
// autocomplete. Each entry: { name, sig (shown as the completion detail), cat
// (category), pop (base popularity → default ordering before the user has any
// personal pick history), kind }.
//
//   kind undefined → inserts `NAME(` with the caret inside the parens (args).
//   kind 'noargs'  → inserts `NAME()`  (a parenthesised no-argument function).
//   kind 'bare'    → inserts `NAME`    (a pseudo-constant used without parens).
//
// Personal usage (lib/funcusage.js) is layered on top of `pop` to rank the
// functions you pick most to the top of a matching search.

const F = (name, sig, cat, pop, kind) => ({ name, sig, cat, pop, kind });

export const FUNCTIONS = [
  // ── Aggregate ──────────────────────────────────────────────────────────────
  F('COUNT', 'COUNT(expr)', 'aggregate', 9),
  F('SUM', 'SUM(expr)', 'aggregate', 9),
  F('AVG', 'AVG(expr)', 'aggregate', 8),
  F('MIN', 'MIN(expr)', 'aggregate', 8),
  F('MAX', 'MAX(expr)', 'aggregate', 8),
  F('GROUP_CONCAT', 'GROUP_CONCAT(expr SEPARATOR str)', 'aggregate', 6),
  F('STD', 'STD(expr)', 'aggregate', 2),
  F('STDDEV', 'STDDEV(expr)', 'aggregate', 2),
  F('STDDEV_POP', 'STDDEV_POP(expr)', 'aggregate', 1),
  F('STDDEV_SAMP', 'STDDEV_SAMP(expr)', 'aggregate', 1),
  F('VARIANCE', 'VARIANCE(expr)', 'aggregate', 1),
  F('VAR_POP', 'VAR_POP(expr)', 'aggregate', 1),
  F('VAR_SAMP', 'VAR_SAMP(expr)', 'aggregate', 1),
  F('BIT_AND', 'BIT_AND(expr)', 'aggregate', 1),
  F('BIT_OR', 'BIT_OR(expr)', 'aggregate', 1),
  F('BIT_XOR', 'BIT_XOR(expr)', 'aggregate', 1),
  F('JSON_ARRAYAGG', 'JSON_ARRAYAGG(expr)', 'aggregate', 2),
  F('JSON_OBJECTAGG', 'JSON_OBJECTAGG(key, value)', 'aggregate', 2),

  // ── Window ─────────────────────────────────────────────────────────────────
  F('ROW_NUMBER', 'ROW_NUMBER() OVER (...)', 'window', 4, 'noargs'),
  F('RANK', 'RANK() OVER (...)', 'window', 3, 'noargs'),
  F('DENSE_RANK', 'DENSE_RANK() OVER (...)', 'window', 3, 'noargs'),
  F('PERCENT_RANK', 'PERCENT_RANK() OVER (...)', 'window', 1, 'noargs'),
  F('CUME_DIST', 'CUME_DIST() OVER (...)', 'window', 1, 'noargs'),
  F('NTILE', 'NTILE(n) OVER (...)', 'window', 1),
  F('LAG', 'LAG(expr, offset, default) OVER (...)', 'window', 3),
  F('LEAD', 'LEAD(expr, offset, default) OVER (...)', 'window', 3),
  F('FIRST_VALUE', 'FIRST_VALUE(expr) OVER (...)', 'window', 2),
  F('LAST_VALUE', 'LAST_VALUE(expr) OVER (...)', 'window', 2),
  F('NTH_VALUE', 'NTH_VALUE(expr, n) OVER (...)', 'window', 1),
  F('GROUPING', 'GROUPING(expr)', 'window', 1),

  // ── Date & time ────────────────────────────────────────────────────────────
  F('NOW', 'NOW()', 'datetime', 8, 'noargs'),
  F('CURDATE', 'CURDATE()', 'datetime', 6, 'noargs'),
  F('CURTIME', 'CURTIME()', 'datetime', 4, 'noargs'),
  F('SYSDATE', 'SYSDATE()', 'datetime', 3, 'noargs'),
  F('CURRENT_TIMESTAMP', 'CURRENT_TIMESTAMP', 'datetime', 6, 'bare'),
  F('CURRENT_DATE', 'CURRENT_DATE', 'datetime', 5, 'bare'),
  F('CURRENT_TIME', 'CURRENT_TIME', 'datetime', 3, 'bare'),
  F('LOCALTIME', 'LOCALTIME', 'datetime', 1, 'bare'),
  F('LOCALTIMESTAMP', 'LOCALTIMESTAMP', 'datetime', 1, 'bare'),
  F('UTC_TIMESTAMP', 'UTC_TIMESTAMP()', 'datetime', 3, 'noargs'),
  F('UTC_DATE', 'UTC_DATE()', 'datetime', 2, 'noargs'),
  F('UTC_TIME', 'UTC_TIME()', 'datetime', 2, 'noargs'),
  F('DATE', 'DATE(expr)', 'datetime', 6),
  F('TIME', 'TIME(expr)', 'datetime', 3),
  F('DATE_FORMAT', 'DATE_FORMAT(date, format)', 'datetime', 8),
  F('TIME_FORMAT', 'TIME_FORMAT(time, format)', 'datetime', 3),
  F('STR_TO_DATE', 'STR_TO_DATE(str, format)', 'datetime', 6),
  F('DATE_ADD', 'DATE_ADD(date, INTERVAL expr unit)', 'datetime', 7),
  F('DATE_SUB', 'DATE_SUB(date, INTERVAL expr unit)', 'datetime', 7),
  F('ADDDATE', 'ADDDATE(date, INTERVAL expr unit)', 'datetime', 3),
  F('SUBDATE', 'SUBDATE(date, INTERVAL expr unit)', 'datetime', 3),
  F('ADDTIME', 'ADDTIME(expr1, expr2)', 'datetime', 2),
  F('SUBTIME', 'SUBTIME(expr1, expr2)', 'datetime', 2),
  F('DATEDIFF', 'DATEDIFF(expr1, expr2)', 'datetime', 6),
  F('TIMEDIFF', 'TIMEDIFF(expr1, expr2)', 'datetime', 4),
  F('TIMESTAMP', 'TIMESTAMP(expr)', 'datetime', 3),
  F('TIMESTAMPADD', 'TIMESTAMPADD(unit, interval, datetime)', 'datetime', 3),
  F('TIMESTAMPDIFF', 'TIMESTAMPDIFF(unit, start, end)', 'datetime', 6),
  F('YEAR', 'YEAR(date)', 'datetime', 6),
  F('QUARTER', 'QUARTER(date)', 'datetime', 2),
  F('MONTH', 'MONTH(date)', 'datetime', 6),
  F('WEEK', 'WEEK(date, mode)', 'datetime', 3),
  F('DAY', 'DAY(date)', 'datetime', 5),
  F('DAYOFMONTH', 'DAYOFMONTH(date)', 'datetime', 3),
  F('DAYOFWEEK', 'DAYOFWEEK(date)', 'datetime', 3),
  F('DAYOFYEAR', 'DAYOFYEAR(date)', 'datetime', 2),
  F('HOUR', 'HOUR(time)', 'datetime', 4),
  F('MINUTE', 'MINUTE(time)', 'datetime', 4),
  F('SECOND', 'SECOND(time)', 'datetime', 4),
  F('MICROSECOND', 'MICROSECOND(expr)', 'datetime', 1),
  F('MONTHNAME', 'MONTHNAME(date)', 'datetime', 3),
  F('DAYNAME', 'DAYNAME(date)', 'datetime', 3),
  F('WEEKDAY', 'WEEKDAY(date)', 'datetime', 2),
  F('WEEKOFYEAR', 'WEEKOFYEAR(date)', 'datetime', 2),
  F('YEARWEEK', 'YEARWEEK(date, mode)', 'datetime', 1),
  F('LAST_DAY', 'LAST_DAY(date)', 'datetime', 4),
  F('MAKEDATE', 'MAKEDATE(year, dayofyear)', 'datetime', 1),
  F('MAKETIME', 'MAKETIME(hour, minute, second)', 'datetime', 1),
  F('PERIOD_ADD', 'PERIOD_ADD(period, months)', 'datetime', 1),
  F('PERIOD_DIFF', 'PERIOD_DIFF(period1, period2)', 'datetime', 1),
  F('SEC_TO_TIME', 'SEC_TO_TIME(seconds)', 'datetime', 2),
  F('TIME_TO_SEC', 'TIME_TO_SEC(time)', 'datetime', 2),
  F('TO_DAYS', 'TO_DAYS(date)', 'datetime', 2),
  F('FROM_DAYS', 'FROM_DAYS(n)', 'datetime', 1),
  F('TO_SECONDS', 'TO_SECONDS(expr)', 'datetime', 1),
  F('UNIX_TIMESTAMP', 'UNIX_TIMESTAMP(date)', 'datetime', 6),
  F('FROM_UNIXTIME', 'FROM_UNIXTIME(unix_ts, format)', 'datetime', 6),
  F('EXTRACT', 'EXTRACT(unit FROM date)', 'datetime', 3),
  F('GET_FORMAT', 'GET_FORMAT(type, format)', 'datetime', 1),
  F('CONVERT_TZ', 'CONVERT_TZ(dt, from_tz, to_tz)', 'datetime', 2),

  // ── String ─────────────────────────────────────────────────────────────────
  F('CONCAT', 'CONCAT(str1, str2, ...)', 'string', 9),
  F('CONCAT_WS', 'CONCAT_WS(sep, str1, str2, ...)', 'string', 6),
  F('LENGTH', 'LENGTH(str)', 'string', 7),
  F('CHAR_LENGTH', 'CHAR_LENGTH(str)', 'string', 5),
  F('CHARACTER_LENGTH', 'CHARACTER_LENGTH(str)', 'string', 1),
  F('OCTET_LENGTH', 'OCTET_LENGTH(str)', 'string', 1),
  F('BIT_LENGTH', 'BIT_LENGTH(str)', 'string', 1),
  F('LEFT', 'LEFT(str, len)', 'string', 6),
  F('RIGHT', 'RIGHT(str, len)', 'string', 6),
  F('SUBSTRING', 'SUBSTRING(str, pos, len)', 'string', 6),
  F('SUBSTR', 'SUBSTR(str, pos, len)', 'string', 5),
  F('MID', 'MID(str, pos, len)', 'string', 2),
  F('SUBSTRING_INDEX', 'SUBSTRING_INDEX(str, delim, count)', 'string', 5),
  F('LOCATE', 'LOCATE(substr, str, pos)', 'string', 4),
  F('POSITION', 'POSITION(substr IN str)', 'string', 2),
  F('INSTR', 'INSTR(str, substr)', 'string', 4),
  F('REPLACE', 'REPLACE(str, from, to)', 'string', 7),
  F('INSERT', 'INSERT(str, pos, len, newstr)', 'string', 2),
  F('REPEAT', 'REPEAT(str, count)', 'string', 3),
  F('REVERSE', 'REVERSE(str)', 'string', 2),
  F('SPACE', 'SPACE(n)', 'string', 1),
  F('LPAD', 'LPAD(str, len, pad)', 'string', 4),
  F('RPAD', 'RPAD(str, len, pad)', 'string', 4),
  F('LTRIM', 'LTRIM(str)', 'string', 3),
  F('RTRIM', 'RTRIM(str)', 'string', 3),
  F('TRIM', 'TRIM([remstr FROM] str)', 'string', 6),
  F('LOWER', 'LOWER(str)', 'string', 6),
  F('UPPER', 'UPPER(str)', 'string', 6),
  F('LCASE', 'LCASE(str)', 'string', 2),
  F('UCASE', 'UCASE(str)', 'string', 2),
  F('STRCMP', 'STRCMP(str1, str2)', 'string', 1),
  F('FIELD', 'FIELD(str, str1, str2, ...)', 'string', 2),
  F('FIND_IN_SET', 'FIND_IN_SET(str, strlist)', 'string', 3),
  F('ELT', 'ELT(n, str1, str2, ...)', 'string', 1),
  F('MAKE_SET', 'MAKE_SET(bits, str1, str2, ...)', 'string', 1),
  F('EXPORT_SET', 'EXPORT_SET(bits, on, off, sep, n)', 'string', 1),
  F('FORMAT', 'FORMAT(number, decimals)', 'string', 4),
  F('ASCII', 'ASCII(str)', 'string', 2),
  F('ORD', 'ORD(str)', 'string', 1),
  F('CHAR', 'CHAR(n, ...)', 'string', 1),
  F('HEX', 'HEX(n_or_str)', 'string', 3),
  F('UNHEX', 'UNHEX(str)', 'string', 2),
  F('BIN', 'BIN(n)', 'string', 1),
  F('OCT', 'OCT(n)', 'string', 1),
  F('CONV', 'CONV(n, from_base, to_base)', 'string', 1),
  F('QUOTE', 'QUOTE(str)', 'string', 1),
  F('SOUNDEX', 'SOUNDEX(str)', 'string', 1),
  F('TO_BASE64', 'TO_BASE64(str)', 'string', 2),
  F('FROM_BASE64', 'FROM_BASE64(str)', 'string', 2),
  F('REGEXP_LIKE', 'REGEXP_LIKE(str, pattern)', 'string', 3),
  F('REGEXP_REPLACE', 'REGEXP_REPLACE(str, pattern, replace)', 'string', 3),
  F('REGEXP_INSTR', 'REGEXP_INSTR(str, pattern)', 'string', 2),
  F('REGEXP_SUBSTR', 'REGEXP_SUBSTR(str, pattern)', 'string', 2),
  F('WEIGHT_STRING', 'WEIGHT_STRING(str)', 'string', 1),
  F('LOAD_FILE', 'LOAD_FILE(path)', 'string', 1),

  // ── Numeric / math ─────────────────────────────────────────────────────────
  F('ABS', 'ABS(x)', 'numeric', 6),
  F('CEIL', 'CEIL(x)', 'numeric', 4),
  F('CEILING', 'CEILING(x)', 'numeric', 3),
  F('FLOOR', 'FLOOR(x)', 'numeric', 5),
  F('ROUND', 'ROUND(x, decimals)', 'numeric', 7),
  F('TRUNCATE', 'TRUNCATE(x, decimals)', 'numeric', 3),
  F('MOD', 'MOD(n, m)', 'numeric', 4),
  F('POW', 'POW(x, y)', 'numeric', 3),
  F('POWER', 'POWER(x, y)', 'numeric', 2),
  F('SQRT', 'SQRT(x)', 'numeric', 3),
  F('EXP', 'EXP(x)', 'numeric', 2),
  F('LN', 'LN(x)', 'numeric', 1),
  F('LOG', 'LOG(b, x)', 'numeric', 2),
  F('LOG2', 'LOG2(x)', 'numeric', 1),
  F('LOG10', 'LOG10(x)', 'numeric', 1),
  F('SIN', 'SIN(x)', 'numeric', 1),
  F('COS', 'COS(x)', 'numeric', 1),
  F('TAN', 'TAN(x)', 'numeric', 1),
  F('ASIN', 'ASIN(x)', 'numeric', 1),
  F('ACOS', 'ACOS(x)', 'numeric', 1),
  F('ATAN', 'ATAN(x)', 'numeric', 1),
  F('ATAN2', 'ATAN2(y, x)', 'numeric', 1),
  F('COT', 'COT(x)', 'numeric', 1),
  F('PI', 'PI()', 'numeric', 1, 'noargs'),
  F('RADIANS', 'RADIANS(x)', 'numeric', 1),
  F('DEGREES', 'DEGREES(x)', 'numeric', 1),
  F('RAND', 'RAND(seed)', 'numeric', 3),
  F('SIGN', 'SIGN(x)', 'numeric', 2),
  F('GREATEST', 'GREATEST(v1, v2, ...)', 'numeric', 4),
  F('LEAST', 'LEAST(v1, v2, ...)', 'numeric', 4),
  F('CRC32', 'CRC32(str)', 'numeric', 1),
  F('BIT_COUNT', 'BIT_COUNT(n)', 'numeric', 1),

  // ── Control flow / comparison ──────────────────────────────────────────────
  F('IF', 'IF(cond, then, else)', 'control', 8),
  F('IFNULL', 'IFNULL(expr, alt)', 'control', 8),
  F('NULLIF', 'NULLIF(expr1, expr2)', 'control', 5),
  F('COALESCE', 'COALESCE(v1, v2, ...)', 'control', 8),
  F('ISNULL', 'ISNULL(expr)', 'control', 3),
  F('INTERVAL', 'INTERVAL(n, n1, n2, ...)', 'control', 2),

  // ── Cast / convert ─────────────────────────────────────────────────────────
  F('CAST', 'CAST(expr AS type)', 'cast', 7),
  F('CONVERT', 'CONVERT(expr, type)', 'cast', 5),
  F('BINARY', 'BINARY expr', 'cast', 2),

  // ── JSON ───────────────────────────────────────────────────────────────────
  F('JSON_EXTRACT', 'JSON_EXTRACT(json, path)', 'json', 5),
  F('JSON_UNQUOTE', 'JSON_UNQUOTE(json)', 'json', 4),
  F('JSON_OBJECT', 'JSON_OBJECT(key, val, ...)', 'json', 4),
  F('JSON_ARRAY', 'JSON_ARRAY(val, ...)', 'json', 3),
  F('JSON_QUOTE', 'JSON_QUOTE(str)', 'json', 2),
  F('JSON_VALUE', 'JSON_VALUE(json, path)', 'json', 3),
  F('JSON_KEYS', 'JSON_KEYS(json, path)', 'json', 2),
  F('JSON_CONTAINS', 'JSON_CONTAINS(target, candidate, path)', 'json', 3),
  F('JSON_CONTAINS_PATH', 'JSON_CONTAINS_PATH(json, one_or_all, path)', 'json', 2),
  F('JSON_SEARCH', 'JSON_SEARCH(json, one_or_all, search)', 'json', 2),
  F('JSON_SET', 'JSON_SET(json, path, val, ...)', 'json', 3),
  F('JSON_INSERT', 'JSON_INSERT(json, path, val, ...)', 'json', 2),
  F('JSON_REPLACE', 'JSON_REPLACE(json, path, val, ...)', 'json', 2),
  F('JSON_REMOVE', 'JSON_REMOVE(json, path, ...)', 'json', 2),
  F('JSON_MERGE_PATCH', 'JSON_MERGE_PATCH(json1, json2, ...)', 'json', 2),
  F('JSON_MERGE_PRESERVE', 'JSON_MERGE_PRESERVE(json1, json2, ...)', 'json', 2),
  F('JSON_TYPE', 'JSON_TYPE(json)', 'json', 2),
  F('JSON_VALID', 'JSON_VALID(str)', 'json', 3),
  F('JSON_LENGTH', 'JSON_LENGTH(json, path)', 'json', 2),
  F('JSON_DEPTH', 'JSON_DEPTH(json)', 'json', 1),
  F('JSON_PRETTY', 'JSON_PRETTY(json)', 'json', 2),
  F('JSON_STORAGE_SIZE', 'JSON_STORAGE_SIZE(json)', 'json', 1),
  F('JSON_OVERLAPS', 'JSON_OVERLAPS(json1, json2)', 'json', 1),
  F('JSON_TABLE', 'JSON_TABLE(expr, path COLUMNS (...))', 'json', 1),

  // ── Encryption / compression / hashing ─────────────────────────────────────
  F('MD5', 'MD5(str)', 'crypto', 4),
  F('SHA1', 'SHA1(str)', 'crypto', 2),
  F('SHA', 'SHA(str)', 'crypto', 1),
  F('SHA2', 'SHA2(str, hash_length)', 'crypto', 3),
  F('AES_ENCRYPT', 'AES_ENCRYPT(str, key)', 'crypto', 2),
  F('AES_DECRYPT', 'AES_DECRYPT(crypt, key)', 'crypto', 2),
  F('RANDOM_BYTES', 'RANDOM_BYTES(len)', 'crypto', 1),
  F('COMPRESS', 'COMPRESS(str)', 'crypto', 1),
  F('UNCOMPRESS', 'UNCOMPRESS(str)', 'crypto', 1),
  F('UNCOMPRESSED_LENGTH', 'UNCOMPRESSED_LENGTH(str)', 'crypto', 1),
  F('STATEMENT_DIGEST', 'STATEMENT_DIGEST(stmt)', 'crypto', 1),
  F('STATEMENT_DIGEST_TEXT', 'STATEMENT_DIGEST_TEXT(stmt)', 'crypto', 1),

  // ── Info / system / misc ───────────────────────────────────────────────────
  F('DATABASE', 'DATABASE()', 'system', 4, 'noargs'),
  F('SCHEMA', 'SCHEMA()', 'system', 2, 'noargs'),
  F('USER', 'USER()', 'system', 3, 'noargs'),
  F('CURRENT_USER', 'CURRENT_USER', 'system', 3, 'bare'),
  F('SESSION_USER', 'SESSION_USER()', 'system', 1, 'noargs'),
  F('SYSTEM_USER', 'SYSTEM_USER()', 'system', 1, 'noargs'),
  F('CURRENT_ROLE', 'CURRENT_ROLE', 'system', 1, 'bare'),
  F('VERSION', 'VERSION()', 'system', 4, 'noargs'),
  F('CONNECTION_ID', 'CONNECTION_ID()', 'system', 2, 'noargs'),
  F('LAST_INSERT_ID', 'LAST_INSERT_ID(expr)', 'system', 5),
  F('ROW_COUNT', 'ROW_COUNT()', 'system', 2, 'noargs'),
  F('FOUND_ROWS', 'FOUND_ROWS()', 'system', 2, 'noargs'),
  F('CHARSET', 'CHARSET(str)', 'system', 1),
  F('COLLATION', 'COLLATION(str)', 'system', 1),
  F('COERCIBILITY', 'COERCIBILITY(str)', 'system', 1),
  F('BENCHMARK', 'BENCHMARK(count, expr)', 'system', 1),
  F('VALUES', 'VALUES(col)', 'system', 2),
  F('UUID', 'UUID()', 'system', 3, 'noargs'),
  F('UUID_SHORT', 'UUID_SHORT()', 'system', 1, 'noargs'),
  F('UUID_TO_BIN', 'UUID_TO_BIN(uuid, swap)', 'system', 2),
  F('BIN_TO_UUID', 'BIN_TO_UUID(bin, swap)', 'system', 2),
  F('INET_ATON', 'INET_ATON(ip)', 'system', 2),
  F('INET_NTOA', 'INET_NTOA(n)', 'system', 2),
  F('INET6_ATON', 'INET6_ATON(ip)', 'system', 1),
  F('INET6_NTOA', 'INET6_NTOA(n)', 'system', 1),
  F('IS_IPV4', 'IS_IPV4(ip)', 'system', 1),
  F('IS_IPV6', 'IS_IPV6(ip)', 'system', 1),
  F('IS_IPV4_COMPAT', 'IS_IPV4_COMPAT(ip)', 'system', 1),
  F('IS_IPV4_MAPPED', 'IS_IPV4_MAPPED(ip)', 'system', 1),
  F('NAME_CONST', 'NAME_CONST(name, value)', 'system', 1),
  F('SLEEP', 'SLEEP(seconds)', 'system', 2),
  F('GET_LOCK', 'GET_LOCK(name, timeout)', 'system', 1),
  F('RELEASE_LOCK', 'RELEASE_LOCK(name)', 'system', 1),
  F('IS_FREE_LOCK', 'IS_FREE_LOCK(name)', 'system', 1),
  F('IS_USED_LOCK', 'IS_USED_LOCK(name)', 'system', 1),
  F('RELEASE_ALL_LOCKS', 'RELEASE_ALL_LOCKS()', 'system', 1, 'noargs'),

  // ── Spatial / GIS ──────────────────────────────────────────────────────────
  F('ST_GeomFromText', 'ST_GeomFromText(wkt, srid)', 'spatial', 1),
  F('ST_GeomFromWKB', 'ST_GeomFromWKB(wkb, srid)', 'spatial', 1),
  F('ST_AsText', 'ST_AsText(g)', 'spatial', 1),
  F('ST_AsBinary', 'ST_AsBinary(g)', 'spatial', 1),
  F('ST_AsGeoJSON', 'ST_AsGeoJSON(g)', 'spatial', 1),
  F('ST_GeomFromGeoJSON', 'ST_GeomFromGeoJSON(json)', 'spatial', 1),
  F('ST_X', 'ST_X(point)', 'spatial', 1),
  F('ST_Y', 'ST_Y(point)', 'spatial', 1),
  F('ST_Latitude', 'ST_Latitude(point)', 'spatial', 1),
  F('ST_Longitude', 'ST_Longitude(point)', 'spatial', 1),
  F('ST_SRID', 'ST_SRID(g, srid)', 'spatial', 1),
  F('ST_Distance', 'ST_Distance(g1, g2)', 'spatial', 1),
  F('ST_Distance_Sphere', 'ST_Distance_Sphere(g1, g2)', 'spatial', 1),
  F('ST_Contains', 'ST_Contains(g1, g2)', 'spatial', 1),
  F('ST_Within', 'ST_Within(g1, g2)', 'spatial', 1),
  F('ST_Intersects', 'ST_Intersects(g1, g2)', 'spatial', 1),
  F('ST_Crosses', 'ST_Crosses(g1, g2)', 'spatial', 1),
  F('ST_Disjoint', 'ST_Disjoint(g1, g2)', 'spatial', 1),
  F('ST_Equals', 'ST_Equals(g1, g2)', 'spatial', 1),
  F('ST_Overlaps', 'ST_Overlaps(g1, g2)', 'spatial', 1),
  F('ST_Touches', 'ST_Touches(g1, g2)', 'spatial', 1),
  F('ST_Area', 'ST_Area(poly)', 'spatial', 1),
  F('ST_Length', 'ST_Length(g)', 'spatial', 1),
  F('ST_Buffer', 'ST_Buffer(g, distance)', 'spatial', 1),
  F('ST_Centroid', 'ST_Centroid(poly)', 'spatial', 1),
  F('ST_ConvexHull', 'ST_ConvexHull(g)', 'spatial', 1),
  F('ST_Envelope', 'ST_Envelope(g)', 'spatial', 1),
  F('ST_Simplify', 'ST_Simplify(g, max_distance)', 'spatial', 1),
  F('ST_IsValid', 'ST_IsValid(g)', 'spatial', 1),
  F('ST_IsSimple', 'ST_IsSimple(g)', 'spatial', 1),
  F('ST_NumPoints', 'ST_NumPoints(linestring)', 'spatial', 1),
  F('ST_PointN', 'ST_PointN(linestring, n)', 'spatial', 1),
  F('ST_StartPoint', 'ST_StartPoint(linestring)', 'spatial', 1),
  F('ST_EndPoint', 'ST_EndPoint(linestring)', 'spatial', 1),
  F('ST_GeometryType', 'ST_GeometryType(g)', 'spatial', 1),
  F('Point', 'Point(x, y)', 'spatial', 1),
  F('LineString', 'LineString(point, ...)', 'spatial', 1),
  F('Polygon', 'Polygon(linestring, ...)', 'spatial', 1),
];
